import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";

import { KeyValue } from "@/main/models/common";
import { getTranslateByKey, isNullUndefined, isValueQueryParams } from "@/main/services/utils";
import { useDinamicFiltersMappingCodes } from "./dinamicFiltersMappingCodes";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCompany, resetReducerCompany, useCompanyReducer } from "@/main/store/company";
import { useDispatch } from "react-redux";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { useAssessmentTypeReducer } from "@/main/store/assessmentType/assessmentTypeHooks";
import { getAssessmentType } from "@/main/store/assessmentType";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";

export const INITIAL_FORM_STATE_ASSESSMENT_TYPE = {
	assessmentType: null as KeyValue | null,
	companyType: null as KeyValue | null,
	company: null as KeyValue | null,
};

export type IFormFiltersMappingTypeAssessmentType = typeof INITIAL_FORM_STATE_ASSESSMENT_TYPE;

interface IFiltersMappingTypeAssessmentTypeProps {}

function FiltersMappingTypeAssessmentType(_props: IFiltersMappingTypeAssessmentTypeProps) {
	//#region Hooks
	const dispatch = useDispatch();
	const { keyReducer, watcherCountry, isRequiredField } = useDinamicFiltersMappingCodes();
	const { control, setValue } = useFormContext<IFormFiltersMappingTypeAssessmentType>();
	//#endregion

	//#region Reducers
	const { assessmentType: assessmentTypeReducer } = useAssessmentTypeReducer();
	const { companyType: companyTypeReducer } = useCompanyTypeReducer();
	const { isolated: companyReducer } = useCompanyReducer(keyReducer);
	//#endregion

	// #region States
	const watcherCompanyType = useWatch({
		control,
		name: "companyType",
	});

	const skipFirstRender = React.useRef(true);
	//#endregion

	// #region Async Functions
	async function callAssessmentTypeApiDomain() {
		dispatch(getAssessmentType());
	}
	async function callCompanyTypeApiDomain() {
		dispatch(getCompanyType());
	}
	async function callCompanyApiDomain(companyTypeSelected: KeyValue | null) {
		if (!isValueQueryParams(companyTypeSelected) && skipFirstRender.current == false) {
			setValue("company", null);
		}
		skipFirstRender.current = false;
		if (isNullUndefined(companyTypeSelected) || isNullUndefined(watcherCountry)) return;

		dispatch(
			getCompany(
				{
					guidCountry: String(watcherCountry.key),
					idCompanyType: String(companyTypeSelected.key),
					idStatusRegister: StatusRegisterEnum.Active,
				},
				keyReducer,
			),
		);
	}
	// #endregion

	useEffect(() => {
		if (isNullUndefined(assessmentTypeReducer.data)) callAssessmentTypeApiDomain();
		if (isNullUndefined(companyTypeReducer.data)) callCompanyTypeApiDomain();

		return () => {
			dispatch(resetReducerCompany());
		};
	}, []);

	useEffect(() => {
		callCompanyApiDomain(watcherCompanyType);
	}, [watcherCountry?.key, watcherCompanyType?.key]);

	const disabledCompany =
		isNullUndefined(watcherCompanyType) ||
		isNullUndefined(watcherCountry) ||
		isNullUndefined(companyReducer.data) ||
		companyReducer.data.length === 1;
	const onlyShowCompanyTypes = [CompanyTypeEnum.BodyShop, CompanyTypeEnum.Insurer];
	const optionsFilteredCompanyType = isNullUndefined(companyTypeReducer.data)
		? null
		: companyTypeReducer.data.filter((item) => onlyShowCompanyTypes.includes(Number(item.key)));
	return (
		<>
			<KogAutoCompleteControlled
				id="id-mappingCode-select-assessmentType"
				nameCustom="assessmentType"
				label={getTranslateByKey("information.assessmentType")}
				isDefaultValueUnique
				control={control}
				setValueCustom={setValue}
				options={assessmentTypeReducer.data}
				isLoading={assessmentTypeReducer.isLoading}
				rulesCustom={{ required: isRequiredField("assessmentType") }}
				onChangeCustom={() => {}}
			/>
			<KogAutoCompleteControlled
				id="id-mappingCode-select-companyType"
				nameCustom="companyType"
				label={getTranslateByKey("information.companyType")}
				isDefaultValueUnique
				control={control}
				setValueCustom={setValue}
				options={optionsFilteredCompanyType}
				isLoading={companyTypeReducer.isLoading}
				rulesCustom={{ required: isRequiredField("companyType") }}
				onChangeCustom={() => {}}
			/>
			<KogAutoCompleteControlled
				id="id-mappingCode-select-company"
				nameCustom="company"
				label={getTranslateByKey("information.company")}
				isDefaultValueUnique
				control={control}
				setValueCustom={setValue}
				options={companyReducer.data}
				isLoading={companyReducer.isLoading}
				disabled={disabledCompany}
				rulesCustom={{ required: isRequiredField("company") }}
				onChangeCustom={() => {}}
			/>
		</>
	);
}

export default FiltersMappingTypeAssessmentType;
